import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import type { RootState } from '../../store/store'
import { LoginResponse, UserResponse } from './types'
import { User } from '../users/types';
import { Logger } from '../../lib/utils';

export interface AuthState {
  token: string | null;
  user: UserResponse | null;
  isAuthenticated: boolean;
  initialized: boolean;
}

const loadAuthState = (): AuthState => {
  try {
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    let user: UserResponse | null = null;

    if (userString) {
      try {
        user = JSON.parse(userString);
        Logger.debug('AuthSlice: Usuario cargado desde localStorage', { role: user?.role });
      } catch (e) {
        console.error("Error parsing user from localStorage", e);
        Logger.error("Error al analizar el usuario desde localStorage:", e);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return { user: null, token: null, isAuthenticated: false, initialized: true };
      }
    }

    if (token && user) {
      Logger.debug('AuthSlice: Sesión válida encontrada en localStorage');
      return { user, token, isAuthenticated: true, initialized: true };
    }
    
    Logger.debug('AuthSlice: No hay sesión válida en localStorage');
    return { user: null, token: null, isAuthenticated: false, initialized: true };
  } catch (error) {
    console.error("Error loading auth state:", error);
    Logger.error("Error al cargar el estado de autenticación:", error);
    return { user: null, token: null, isAuthenticated: false, initialized: true };
  }
};

const initialState: AuthState = loadAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      state.initialized = true;
      
      Logger.debug('AuthSlice: Guardando credenciales en localStorage', { role: user?.role });
      
      // Guardar en localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    },
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.initialized = true;
      
      Logger.debug('AuthSlice: Cerrando sesión, eliminando datos de localStorage');
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    initializeAuth: (state) => {
      const loadedState = loadAuthState();
      state.token = loadedState.token;
      state.user = loadedState.user;
      state.isAuthenticated = loadedState.isAuthenticated;
      state.initialized = true;
      
      Logger.debug(`AuthSlice: Inicialización completada - Autenticado: ${loadedState.isAuthenticated}`);
    },
    updateUserEmpresa: (
      state,
      { payload }: PayloadAction<User>
    ) => {
      if (state.user) {
        state.user = {
          ...state.user,
          empresa_id: payload.empresa_id || undefined,
          empresaId: payload.empresa_id || undefined,
          bot1_enabled: payload.bot1_enabled ?? state.user.bot1_enabled,
          bot2_enabled: payload.bot2_enabled ?? state.user.bot2_enabled,
          bot3_enabled: payload.bot3_enabled ?? state.user.bot3_enabled,
          bot4_enabled: payload.bot4_enabled ?? state.user.bot4_enabled,
          bot5_enabled: payload.bot5_enabled ?? state.user.bot5_enabled,
          bot6_enabled: payload.bot6_enabled ?? state.user.bot6_enabled,
          bot7_enabled: payload.bot7_enabled ?? state.user.bot7_enabled,
          bot8_enabled: payload.bot8_enabled ?? state.user.bot8_enabled,
          bot9_enabled: payload.bot9_enabled ?? state.user.bot9_enabled,
          bot10_enabled: payload.bot10_enabled ?? state.user.bot10_enabled,
          clientes_enabled: payload.clientes_enabled ?? state.user.clientes_enabled
        };
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
});

export const { setCredentials, logout, initializeAuth, updateUserEmpresa } = authSlice.actions;

export default authSlice.reducer;

// Selectores
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsInitialized = (state: RootState) => state.auth.initialized;
export const selectUserRole = (state: RootState) => state.auth.user?.role;

// Selectores derivados
export const selectBotPermissions = createSelector(
  [selectCurrentUser],
  (user) => {
    if (!user) return null;
    
    return {
      bot1_enabled: !!user.bot1_enabled,
      bot2_enabled: !!user.bot2_enabled,
      bot3_enabled: !!user.bot3_enabled,
      bot4_enabled: !!user.bot4_enabled,
      bot5_enabled: !!user.bot5_enabled,
      bot6_enabled: !!user.bot6_enabled,
      bot7_enabled: !!user.bot7_enabled,
      bot8_enabled: !!user.bot8_enabled,
      bot9_enabled: !!user.bot9_enabled,
      bot10_enabled: !!user.bot10_enabled,
      clientes_enabled: !!user.clientes_enabled,
    };
  }
);

export const selectHasBotPermission = (state: RootState, botNumber: number) => {
  const user = state.auth.user;
  if (!user) return false;
  return Boolean(user[`bot${botNumber}_enabled` as keyof typeof user]);
};

export const selectIsAdmin = createSelector(
  [selectUserRole],
  (role) => role === 'admin' || role === 'superadmin'
);

export const selectIsSuperAdmin = createSelector(
  [selectUserRole],
  (role) => role === 'superadmin'
); 